import React, { useState } from 'react';
import { Axios } from '@helpers/ApiService';
import { useNavigate } from 'react-router-dom';
import {
  errorMessage,
  fileUploadConfig,
  onboardingStepId,
  responseTexts,
  storeSettingsSubsteps,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { useToast } from '@contexts/Toast';
import { useFileDropZone } from '@helpers/FileDropZone';
import { CustomerOrderService } from '@services/CustomerService';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { OnBoardSettings } from '@components/OnBoardSettings';

export const StoreSettingsLogoUpload: React.FC = () => {
  const { updateOnboardingStep } = useLocalStorage();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const butcherDetails = JSON.parse(
    localStorage.getItem('butcherDetails') || '{}',
  );

  const { isDragging, handleDragOver, handleDragLeave, handleDrop } =
    useFileDropZone({ setFile, setError });

  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        if (selectedFile.size > fileUploadConfig.MAX_FILE_SIZE) {
          setError(errorMessage.MAX_FILE_SIZE_ERROR);
        } else if (!/\.(jpg|png)$/i.test(selectedFile.name)) {
          setError(errorMessage.FILE_FORMATS);
        } else {
          setError('');
          setFile(selectedFile);
        }
      }
    } catch {
      setError(errorMessage.FILE_SELECTION_ERROR);
    }
  };

  // UPDATED: handleSubmit now allows proceeding if a logo already exists
  const handleSubmit: (
    event: React.FormEvent,
    nextPage: boolean,
  ) => Promise<void> = async (event, nextPage) => {
    event.preventDefault();

    // Check if there's an existing logo in localStorage
    const hasExistingLogo = !!butcherDetails?.profileImage?.logoLocation;

    // Allow if either a file is selected OR a logo already exists
    if (!file && !hasExistingLogo) {
      setError(errorMessage.UPLOAD_REQUIRED);
      return;
    }

    // Clear any previous error
    setError('');

    try {
      if (file) {
        // Upload new logo only if a new file was selected
        const formData = new FormData();
        formData.append('logo', file);

        const response = await Axios.post(ApiEndpoints.CREATE_LOGO, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.status === 'success') {
          const butcherDetails = localStorage.getItem('butcherDetails');
          let details = butcherDetails ? JSON.parse(butcherDetails) : {};
          details = {
            ...details,
            profileImage: {
              ...(details.profileImage || {}),
              logoLocation: response.data.data.logoImageUrl,
            },
          };
          CustomerOrderService.setButcherDetails(details);
          showToastMessage(responseTexts.UPLOAD_SUCCESS, 'success');
        } else {
          setError(errorMessage.UPLOAD_FAILED);
          return;
        }
      }
      // If no file but existing logo, skip upload and proceed

      // Update onboarding step and navigate if requested
      updateOnboardingStep(onboardingStepId.LOGO);
      if (nextPage) {
        navigate(FrontendRoutes.STORE_ADDRESS, {
          state: { fromSettings: true },
        });
      }
    } catch {
      setError(errorMessage.UPLOAD_FAILED);
    }
  };

  const handleNext = (event: React.FormEvent) => {
    handleSubmit(event, true);
  };

  const renderContent = (
    <div className="flex flex-col min-h-screen">
      {/* Breadcrumbs */}
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Store"
          activeStep={1}
          subNavSteps={storeSettingsSubsteps}
          fromSettings={true}
          forStoreSetting={true}
        />
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {/* Company Details */}
        <div className="mt-4 text-left text-gray-600 flex items-center">
          {butcherDetails?.profileImage?.logoLocation ? (
            <img
              src={butcherDetails.profileImage.logoLocation}
              alt="Store Logo"
              className="w-10 h-10 border border-gray-300 rounded-full mr-3 object-cover"
            />
          ) : (
            <div className="w-10 h-10 bg-gray-300 border border-gray-300 rounded-full mr-3"></div>
          )}
          <div>
            <p className="text-md text-black">
              {butcherDetails?.companyName}
            </p>
            <p className="text-sm">
              Select and upload the files from your computer
            </p>
          </div>
        </div>

        <form>
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center my-4 transition-colors duration-200 ${
              isDragging
                ? 'border-primary bg-primary-50'
                : 'border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file"
              className="hidden"
              accept=".jpg,.png"
              onChange={handleFileChange}
            />
            <p className="font-semibold text-gray-700">
              Choose a file or drag & drop it here
            </p>
            <p className="text-gray-500 text-sm mt-1">
              JPEG, PNG formats up to 2 MB
            </p>
            <label
              htmlFor="file"
              className="cursor-pointer mt-3 inline-block py-1 px-4 text-sm font-medium border border-gray-400 text-gray-700 rounded-sm hover:bg-primary hover:text-white"
            >
              Browse File
            </label>
            {error && <p className="text-red-500 mt-2">{error}</p>}
          </div>
        </form>

        {file && !error && (
          <div className="mt-4 bg-gray-200 p-3 rounded-sm relative">
            <button
              onClick={() => setFile(null)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 focus:outline-none"
              type="button"
            >
              &times;
            </button>
            <div className="flex items-center gap-3">
              <img
                src={URL.createObjectURL(file)}
                alt="Uploaded preview"
                style={{
                  width: '100px',
                  height: '100px',
                  objectFit: 'cover',
                }}
                className="border rounded"
              />
              <div className="flex-1">
                <p className="text-gray-700 font-medium">{file.name}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky footer – always at bottom */}
      <div className="sticky bottom-0 bg-white border-t border-gray-100 z-10 px-4 py-4">
        <div className="flex flex-col sm:flex-row items-center justify-end gap-4">
          {/* Save – disabled when no file, because there is nothing new to save */}
          <button
            onClick={(e) => handleSubmit(e, false)}
            disabled={!file}
            className={`w-full sm:w-[130px] h-10 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${
              file
                ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480] pointer-events-auto'
            }`}
            type="button"
          >
            Save
          </button>

          {/* Next – always enabled; validation inside handleSubmit handles the existing logo case */}
          <button
            onClick={handleNext}
            className="w-full sm:w-[150px] h-10 px-6 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2 transition-colors duration-200"
            type="button"
          >
            <span>Next</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <OnBoardSettings renderContent={renderContent} />
    </>
  );
};